import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Image, Pressable, StyleSheet, Text, View } from 'react-native';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { pickImageFromLibrary, takePhotoFromCamera } from '@/utils/media/imagePicker';
import theme from '@/config/theme';

type PartnerPhotoFieldProps = {
  value: string | null;
  editable: boolean;
  busy?: boolean;
  onSelect: (blob: Blob, mimeType: string) => void;
  onRemove: () => void;
};

const PartnerPhotoField = ({ value, editable, busy = false, onSelect, onRemove }: PartnerPhotoFieldProps) => {
  const { showActionSheetWithOptions } = useActionSheet();
  const [opening, setOpening] = useState(false);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const mounted = useRef(false);
  const selecting = useRef(false);
  const current = useRef({ editable, busy, onSelect });
  current.current = { editable, busy, onSelect };
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const isCurrent = () => mounted.current && current.current.editable && !current.current.busy;
  const choose = async (index?: number) => {
    const camera = index === 1;
    try {
      if (!isCurrent() || (index !== 1 && index !== 2)) return;
      const picker = camera ? takePhotoFromCamera : pickImageFromLibrary;
      const outcome = await picker({ mediaTypes: ['images'], allowsEditing: false, aspect: [4, 3], quality: 0.7 }, {
        source: 'partner_photo_field', screen: 'public/partner', action: camera ? 'take_photo' : 'pick_photo',
      });
      if (!isCurrent() || outcome.status === 'cancelled') return;
      if (outcome.status === 'denied' || outcome.status === 'failed') {
        throw new Error('picker_unavailable');
      }
      const asset = outcome.result.assets[0];
      if (!asset) return;
      let blob: Blob;
      if (asset.file) blob = asset.file;
      else {
        const response = await fetch(asset.uri);
        if (!response.ok) throw new Error('image_unavailable');
        blob = await response.blob();
      }
      if (isCurrent()) current.current.onSelect(blob, blob.type || asset.mimeType || 'image/jpeg');
    } catch {
      if (isCurrent()) setSelectionError(camera
        ? 'Não foi possível usar a câmera. Confira a permissão do navegador ou escolha Galeria.'
        : 'Não foi possível selecionar a foto. Tente abrir a Galeria novamente.');
    } finally {
      selecting.current = false;
      if (mounted.current) setOpening(false);
    }
  };

  const openPicker = () => {
    if (selecting.current || !isCurrent()) return;
    selecting.current = true;
    setOpening(true);
    setSelectionError(null);
    showActionSheetWithOptions({
      options: ['Cancelar', 'Tirar foto', 'Galeria'], cancelButtonIndex: 0,
      title: 'Selecionar foto', message: 'De onde você quer obter a foto?',
    }, index => { void choose(index); });
  };

  return (
    <View style={styles.container}>
      {value ? <Image source={{ uri: value }} style={styles.preview} resizeMode="contain" /> : (
        <View style={styles.empty}><Text style={styles.emptyText}>Nenhuma imagem enviada</Text></View>
      )}
      {editable && (
        <View style={styles.actions}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Tirar ou selecionar foto"
            disabled={busy || opening}
            onPress={openPicker}
            style={[styles.button, (busy || opening) && styles.disabled]}
          >
            {busy || opening ? <ActivityIndicator color={theme.colors.primary.default} /> : (
              <Text style={styles.buttonText}>{value ? 'Trocar foto' : 'Adicionar foto'}</Text>
            )}
          </Pressable>
          {value && <Pressable accessibilityRole="button" disabled={busy || opening} onPress={onRemove}>
            <Text style={styles.removeText}>Remover</Text>
          </Pressable>}
        </View>
      )}
      {selectionError && <Text accessibilityRole="alert" style={styles.errorText}>{selectionError}</Text>}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { gap: theme.spacing.sm },
  preview: { width: '100%', height: 220, backgroundColor: theme.colors.background.tertiary, borderRadius: theme.borderRadius.md },
  empty: { minHeight: 96, justifyContent: 'center', alignItems: 'center', borderWidth: 1, borderColor: theme.colors.border.light, borderStyle: 'dashed', borderRadius: theme.borderRadius.md },
  emptyText: { color: theme.colors.text.secondary, fontSize: theme.fontSizes.sm },
  actions: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.md },
  button: { minHeight: 42, paddingHorizontal: theme.spacing.md, borderRadius: theme.borderRadius.sm, borderWidth: 1, borderColor: theme.colors.primary.default, justifyContent: 'center', alignItems: 'center' },
  disabled: { opacity: 0.6 },
  buttonText: { color: theme.colors.primary.dark, fontWeight: '600' },
  removeText: { color: theme.colors.util.error, fontWeight: '500' },
  errorText: { color: theme.colors.util.error, fontSize: theme.fontSizes.sm },
});

export default PartnerPhotoField;
